import { useEffect, useState } from 'react';
import { Loader2, Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getAdminMemberDetail, updateMember } from '@/api/member';
import { getCycleDateRange } from '@/utils/cycle-dates';
import { getMemberCycleRemaining, getMemberUsedBorrowedQuota } from '@shared/member-quota';
import type { Member } from '@shared/api.interface';
import { extractApiErrorMessage } from './api-error';

type AdjustMode = 'deduct' | 'borrow' | 'fix';

const MODE_OPTIONS: Array<{ id: AdjustMode; label: string; hint: string }> = [
  { id: 'deduct', label: '扣减次数', hint: '微信代发后扣减，算作会员已领' },
  { id: 'borrow', label: '预支下周期', hint: '本周期不够用时先借，下周期只扣实际用掉的' },
];

const REASON_PRESETS: Record<AdjustMode, string[]> = {
  deduct: ['微信代发资源', '老客户不会操作，代为领取'],
  borrow: ['本周期额度不够，提前预支'],
  fix: ['数据录入错误修正'],
};

interface QuotaAdjustDialogProps {
  member: Member | null;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}

const toCount = (value: string): number | null => {
  if (!/^\d+$/.test(value.trim())) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : null;
};

export const QuotaAdjustDialog = ({ member: initialMember, onClose, onSaved }: QuotaAdjustDialogProps) => {
  const [member, setMember] = useState<Member | null>(initialMember);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [mode, setMode] = useState<AdjustMode>('deduct');
  const [amount, setAmount] = useState('1');
  const [note, setNote] = useState('');
  const [fixCounts, setFixCounts] = useState({ cycle_used: '', today_used: '', total_redemptions: '' });
  const [isSaving, setIsSaving] = useState(false);

  const loadMember = async (passcode: string) => {
    setIsRefreshing(true);
    try {
      const detail = await getAdminMemberDetail(passcode);
      setMember(detail.member);
      return detail.member;
    } catch {
      // 拿不到最新数据时沿用列表数据，保存时后端会用额度版本号兜底校验
      return null;
    } finally {
      setIsRefreshing(false);
    }
  };

  const resetForm = (target: Member) => {
    setAmount('1');
    setNote('');
    setFixCounts({
      cycle_used: String(target.cycle_used),
      today_used: String(target.today_used),
      total_redemptions: String(target.total_redemptions),
    });
  };

  useEffect(() => {
    setMember(initialMember);
    setMode('deduct');
    if (!initialMember) return;
    resetForm(initialMember);
    // 打开时拉一次最新额度，避免列表数据过期导致数字不准
    void loadMember(initialMember.passcode).then((fresh) => fresh && resetForm(fresh));
  }, [initialMember]);

  if (!initialMember || !member) return null;

  const isTotalMode = member.quota_mode === 'total';
  const isActive = member.status === 'active';
  const remaining = getMemberCycleRemaining(member);
  const todayLeft = member.daily_limit > 0
    ? Math.max(0, Math.min(member.daily_limit - member.today_used, remaining))
    : remaining;
  const borrowed = member.next_cycle_borrowed ?? 0;
  const borrowedUsed = getMemberUsedBorrowedQuota(member);
  const borrowLimit = member.next_cycle_remaining ?? 0;
  const cycle = getCycleDateRange(member.cycle_start_date);

  const count = toCount(amount);
  const maxAmount = mode === 'deduct' ? remaining : borrowLimit;
  const modeOptions = isTotalMode ? MODE_OPTIONS.filter((option) => option.id !== 'borrow') : MODE_OPTIONS;

  let blockedReason = '';
  if (mode === 'deduct') {
    if (!isActive) blockedReason = '会员不是启用状态，不能扣减。';
    else if (remaining === 0) blockedReason = isTotalMode ? '总次数已用完，没有可扣减的次数。' : '本周期已没有剩余次数。如需代发，请先预支下周期。';
  } else if (mode === 'borrow') {
    if (!isActive) blockedReason = '会员不是启用状态，不能预支。';
    else if (!member.can_borrow_next_cycle) blockedReason = '会员有效期到不了下个周期，不能预支。';
    else if (borrowLimit === 0) blockedReason = '下周期的额度已经全部预支完了。';
  }

  const amountError = mode !== 'fix' && !blockedReason && (count === null || count < 1 || count > maxAmount)
    ? `请输入 1 到 ${maxAmount} 之间的整数`
    : '';

  const fixValues = {
    cycle_used: toCount(fixCounts.cycle_used),
    today_used: toCount(fixCounts.today_used),
    total_redemptions: toCount(fixCounts.total_redemptions),
  };
  const fixInvalid = Object.values(fixValues).some((value) => value === null);
  const fixChanged = !fixInvalid && (
    fixValues.cycle_used !== member.cycle_used ||
    fixValues.today_used !== member.today_used ||
    fixValues.total_redemptions !== member.total_redemptions
  );

  const canSubmit = !isSaving && !isRefreshing && !!note.trim() && (
    mode === 'fix' ? fixChanged : !blockedReason && !amountError
  );

  const stepAmount = (delta: number) => {
    const next = Math.min(Math.max(1, (count ?? 0) + delta), Math.max(1, maxAmount));
    setAmount(String(next));
  };

  const switchMode = (next: AdjustMode) => {
    setMode(next);
    setAmount('1');
    setNote('');
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSaving(true);
    try {
      await updateMember({
        passcode: member.passcode,
        expected_quota_revision: member.quota_revision,
        adjustment_note: note.trim(),
        ...(mode === 'deduct' ? { deduct_quota: count! } : {}),
        ...(mode === 'borrow' ? { borrow_next_cycle: count! } : {}),
        ...(mode === 'fix' ? {
          cycle_used: fixValues.cycle_used !== member.cycle_used ? fixValues.cycle_used! : undefined,
          today_used: fixValues.today_used !== member.today_used ? fixValues.today_used! : undefined,
          total_redemptions: fixValues.total_redemptions !== member.total_redemptions ? fixValues.total_redemptions! : undefined,
        } : {}),
      });
      toast.success(mode === 'deduct' ? `已扣减 ${count} 次` : mode === 'borrow' ? `已预支 ${count} 次` : '计数已修正');
      onClose();
      await onSaved();
    } catch (error) {
      toast.error(extractApiErrorMessage(error, '调整额度失败'));
      // 额度可能已被会员领取改变，刷新后让管理员看着最新数字再提交
      const fresh = await loadMember(member.passcode);
      if (fresh) setFixCounts({
        cycle_used: String(fresh.cycle_used),
        today_used: String(fresh.today_used),
        total_redemptions: String(fresh.total_redemptions),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const n = count ?? 0;
  const willExceedDaily = mode === 'deduct' && member.daily_limit > 0 && member.today_used + n > member.daily_limit;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>调整额度</DialogTitle>
          <DialogDescription className="break-all font-mono">{member.passcode}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* 现状一览 */}
          <div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-xl bg-muted px-2 py-3">
                <div className="text-2xl font-semibold">{remaining}</div>
                <div className="mt-1 text-xs text-muted-foreground">{isTotalMode ? '剩余总次数' : '本周期剩余'}</div>
              </div>
              <div className="rounded-xl bg-muted px-2 py-3">
                <div className="text-2xl font-semibold">{todayLeft}</div>
                <div className="mt-1 text-xs text-muted-foreground">今日还可领</div>
              </div>
              <div className="rounded-xl bg-muted px-2 py-3">
                <div className="text-2xl font-semibold">{isTotalMode ? member.total_redemptions : borrowed}</div>
                <div className="mt-1 text-xs text-muted-foreground">{isTotalMode ? '累计已领' : '已预支'}</div>
              </div>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {isTotalMode
                ? `总额度 ${member.total_quota ?? 0} 次，不按周期恢复。`
                : `每周期 ${member.monthly_quota} 次${cycle ? `，本周期 ${cycle.startLabel}–${cycle.endLabel}，${cycle.nextResetDate} 恢复` : ''}。`}
              {!isTotalMode && borrowed > 0 && ` 预支的 ${borrowed} 次里已用 ${borrowedUsed} 次，下周期扣 ${borrowedUsed} 次。`}
              {isRefreshing && ' 正在刷新…'}
            </p>
          </div>

          {/* 调整方式 */}
          {mode !== 'fix' && (
            <>
              <div className={`grid gap-2 ${modeOptions.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`} role="radiogroup" aria-label="调整方式">
                {modeOptions.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    role="radio"
                    aria-checked={mode === option.id}
                    onClick={() => switchMode(option.id)}
                    className={`rounded-xl border px-3 py-2.5 text-left transition-colors duration-150 ${
                      mode === option.id ? 'border-primary bg-primary/10' : 'border-border hover:bg-accent'
                    }`}
                  >
                    <div className={`text-sm font-semibold ${mode === option.id ? 'text-primary' : ''}`}>{option.label}</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">{option.hint}</div>
                  </button>
                ))}
              </div>

              {blockedReason ? (
                <p className="rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">{blockedReason}</p>
              ) : (
                <div className="space-y-2">
                  <label htmlFor="quota-adjust-amount" className="block text-sm font-medium">
                    {mode === 'deduct' ? '扣减几次' : '预支几次'}
                    <span className="ml-1 font-normal text-muted-foreground">（最多 {maxAmount} 次）</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <Button type="button" variant="outline" size="icon" className="h-11 w-11" aria-label="减少"
                      onClick={() => stepAmount(-1)} disabled={n <= 1}>
                      <Minus className="h-4 w-4" />
                    </Button>
                    <Input id="quota-adjust-amount" inputMode="numeric" className="h-11 w-24 text-center text-lg"
                      value={amount} onChange={(e) => setAmount(e.target.value)} />
                    <Button type="button" variant="outline" size="icon" className="h-11 w-11" aria-label="增加"
                      onClick={() => stepAmount(1)} disabled={n >= maxAmount}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {amountError ? (
                    <p className="text-sm text-destructive">{amountError}</p>
                  ) : (
                    <div className="rounded-lg bg-primary/5 px-3 py-2 text-sm">
                      {mode === 'deduct' ? (
                        <>
                          <p>{isTotalMode ? '剩余总次数' : '本周期剩余'} {remaining} → <b>{remaining - n}</b></p>
                          <p>今日已领 {member.today_used} → <b>{member.today_used + n}</b></p>
                          {willExceedDaily && (
                            <p className="mt-1 text-amber-700">超过单日上限 {member.daily_limit} 次，会员今天将不能再自己领取。</p>
                          )}
                        </>
                      ) : (
                        <>
                          <p>本周期剩余 {remaining} → <b>{remaining + n}</b></p>
                          <p className="text-muted-foreground">下周期只扣实际用掉的部分，没用完的自动退回。</p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {mode === 'fix' && (
            <div className="space-y-3">
              <p className="rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
                直接改计数，只在数据出错时使用（例如扣多了要改回来）。
              </p>
              {([
                ...(isTotalMode ? [] : [['cycle_used', '本周期已领'] as const]),
                ['today_used', '今日已领'] as const,
                ['total_redemptions', isTotalMode ? '总次数已领' : '累计已领'] as const,
              ]).map(([field, label]) => (
                <div key={field} className="flex items-center gap-3">
                  <label htmlFor={`quota-fix-${field}`} className="w-24 shrink-0 text-sm font-medium">{label}</label>
                  <Input id={`quota-fix-${field}`} inputMode="numeric" className="h-11"
                    value={fixCounts[field]}
                    onChange={(e) => setFixCounts({ ...fixCounts, [field]: e.target.value })} />
                </div>
              ))}
              {fixInvalid && <p className="text-sm text-destructive">请填写大于等于 0 的整数</p>}
            </div>
          )}

          {/* 原因 */}
          <div className="space-y-2">
            <label htmlFor="quota-adjust-note" className="block text-sm font-medium">
              原因 <span className="text-destructive">*</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {REASON_PRESETS[mode].map((preset) => (
                <button key={preset} type="button" onClick={() => setNote(preset)}
                  className={`rounded-full px-3 py-1 text-xs transition-colors duration-150 ${
                    note === preset ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'
                  }`}>
                  {preset}
                </button>
              ))}
            </div>
            <Textarea id="quota-adjust-note" value={note} maxLength={500} rows={2}
              placeholder="点上面的常用原因，或自己填写" onChange={(e) => setNote(e.target.value)} />
          </div>

          <button type="button" className="text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
            onClick={() => switchMode(mode === 'fix' ? 'deduct' : 'fix')}>
            {mode === 'fix' ? '← 返回扣减 / 预支' : '数据出错？直接修正计数'}
          </button>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>取消</Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isSaving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {mode === 'deduct' ? `确认扣减${n > 0 && !amountError ? ` ${n} 次` : ''}`
              : mode === 'borrow' ? `确认预支${n > 0 && !amountError ? ` ${n} 次` : ''}`
              : '保存修正'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
